import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import type { Apartment, Property } from '~/types/properties';

export const nameToColorPropertyMap: { [key: string]: string } = {
  'On Sale': '#3957D3',
  Launch: '#F0C724',
  'Presale (EOI)': '#43CF58',
  Announcement: '#F6865F',
  Pending: '#EE4925',
  'Sold Out': '#CFCFD0',
};

const codeToNameMap: { [key: string]: string } = {
  rooms_studio: 'Studio',
  rooms_1: '1 bedroom',
  rooms_2: '2 bedroom',
  rooms_3: '3 bedroom',
  rooms_4: '4 bedroom',
  rooms_5: '5 bedroom',
  rooms_6: '6 bedroom',
  rooms_7: '7 bedroom',
};

const nameToCodeMap: { [key: string]: string } = {
  Studio: 'rooms_studio',
  '1 Bedroom': 'rooms_1',
  '1 BR': 'rooms_1',
  '2 Bedroom': 'rooms_2',
  '2 BR': 'rooms_2',
  '3 Bedroom': 'rooms_3',
  '3 BR': 'rooms_3',
  '4 Bedroom': 'rooms_4',
  '4 BR': 'rooms_4',
  '5 Bedroom': 'rooms_5',
  '5 BR': 'rooms_5',
  '6 Bedroom': 'rooms_6',
  '6 BR': 'rooms_6',
  '7 Bedroom': 'rooms_7',
  '7 BR': 'rooms_7',
};

export const getPropertyUnits = (items: Apartment[]): any => {
  const apartments = items.map((item: Apartment) => {
    let key: string | undefined;

    if (item.rooms_count?.en && nameToCodeMap[item.rooms_count.en]) {
      key = nameToCodeMap[item.rooms_count.en];
    } else if (item.title?.en && nameToCodeMap[item.title.en]) {
      key = nameToCodeMap[item.title.en];
    } else {
      key = 'unknown';
    }

    return {
      key,
      ...item,
    };
  });

  const units = map(
    groupBy(apartments, (apartment) => apartment.key),
    (value, key) => ({
      name: key,
      title: codeToNameMap[key] || key,
      price_min: Math.min(...value.map((apartment) => parseFloat(apartment.price.min))),
      area_min: Math.min(...value.map((apartment) => parseFloat(apartment.area_min?.m2))),
      area_max: Math.max(...value.map((apartment) => parseFloat(apartment.area_max?.m2))),
      count: value.reduce((sum, apartment) => sum + parseInt(apartment.sale_units_count, 10), 0),
      currency: value[0].price.currency,
      items: value,
    }),
  );
  const sortOrder = Object.keys(codeToNameMap);
  const sortedUnits = units
    .filter((unit) => unit.name !== 'unknown')
    .sort((a, b) => {
      return sortOrder.indexOf(a.name) - sortOrder.indexOf(b.name);
    });
  return sortedUnits;
};

/**
 * Build a per-unit picker list (one row per purchasable apartment) for the
 * commission calculator. Falls back to bucket grouping via `getPropertyUnits`
 * if no apartments are flagged purchasable.
 *
 * Filter intent: include ANY unit that an agent could still close on — that
 * means `available`, `on sale`, AND `offplan` (most off-plan project units
 * arrive with status='offplan' from buildUnitMixFromOffplan and that's
 * the dominant case for this portal). Only sold / reserved / on-hold get
 * filtered out. The Availability tab uses the same intent.
 */
const NON_PURCHASABLE_STATUSES = ['sold', 'reserved', 'reserv', 'hold', 'on hold', 'on_hold', 'cancelled', 'canceled'];

export const getPropertyAvailableUnits = (
  items: Apartment[],
): Array<{ name: string; title: string; price_min: number; currency?: string }> => {
  if (!items?.length) return [];
  const rows = items
    .map((apt, idx) => {
      const status = (apt as { status?: string }).status?.toLowerCase();
      if (status && NON_PURCHASABLE_STATUSES.some((s) => status.includes(s))) {
        return null;
      }
      const price = parseFloat(apt.price?.min ?? '');
      if (!Number.isFinite(price) || price <= 0) return null;
      const id = (apt as { id?: string | number }).id;
      const name = id != null ? String(id) : `unit-${idx}`;
      const roomsLabel = apt.rooms_count?.en?.trim() || apt.title?.en?.trim() || 'Unit';
      const areaM2 = parseFloat((apt.area_min?.m2 ?? apt.area_max?.m2) as never);
      const titleParts = [roomsLabel];
      if (Number.isFinite(areaM2) && areaM2 > 0) titleParts.push(`${Math.round(areaM2)} m²`);
      return {
        name,
        title: titleParts.join(' · '),
        price_min: price,
        currency: apt.price?.currency,
      };
    })
    .filter((row): row is NonNullable<typeof row> => row !== null);
  rows.sort((a, b) => a.price_min - b.price_min);
  return rows;
};

export const formatPropertyType = (type: string | null | undefined): string => {
  if (!type) return 'Apartment';

  return type
    .split(/[_\s-]+/)
    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Default builder matches the original hardcoded English "Q{quarter} {year}" —
// callers that need a translated string pass their own `t('property.shared.quarterYear', …)`
// closure instead (this helper can't call useI18n directly).
const defaultQuarterYearBuilder = (quarter: number, year: number) => `Q${quarter} ${year}`;

export const formatCompletionDate = (
  date: string | null | undefined,
  formatQuarterYear: (quarter: number, year: number) => string = defaultQuarterYearBuilder,
): string | null => {
  if (!date) return null;
  // Guest-mode payloads are pre-redacted to `Q? YYYY` strings (see
  // server/utils/properties/redactProperty.ts). `new Date("Q2 2027")` is
  // Invalid, which used to render as "QNaN NaN" — parse the pre-formatted
  // value back into parts instead of passing it through verbatim, so callers
  // that pass a translated builder still get a localized string here too.
  const redacted = date.match(/^Q([1-4])\s+(\d{4})$/);
  if (redacted) return formatQuarterYear(Number(redacted[1]), Number(redacted[2]));
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return null;
  const quarter = Math.floor(d.getMonth() / 3) + 1;
  const year = d.getFullYear();
  return formatQuarterYear(quarter, year);
};

export const sortPropertiesByField = (
  properties: Property[],
  orderBy: string = 'updated_at',
  orderWay: string = 'desc',
): Property[] => {
  const getPropertyValue = (property: Property, field: string): number | string => {
    switch (field) {
      case 'updated_at':
        return new Date(property.updated_at || 0).getTime();

      case 'created_at':
        return new Date((property as any).created_at || property.updated_at || 0).getTime();

      case 'price':
        return Number(property.price?.min || 0);

      case 'title':
        return (property.title as any)?.en || '';

      default:
        return 0;
    }
  };

  const compareValues = (a: number | string, b: number | string, direction: string): number => {
    if (a === b) return 0;

    const comparison = a > b ? 1 : -1;
    return direction === 'asc' ? comparison : -comparison;
  };

  return [...properties].sort((a, b) => {
    const aValue = getPropertyValue(a, orderBy);
    const bValue = getPropertyValue(b, orderBy);
    return compareValues(aValue, bValue, orderWay);
  });
};
